import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatListBulleted,
  FormatListNumbered,
  FormatTextdirectionLToR,
  FormatTextdirectionRToL,
  TableChartOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useState, memo, useRef } from 'react';
import { ccfEditorToolbarStyles } from './ccf-lexical-toolbar-styles';
import styles from './ccf-digital-email-template.module.scss';
import {
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  $getSelection,
  RangeSelection,
  $isParagraphNode,
  TextNode,
  ElementNode,
  $createTextNode,
  $insertNodes,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $findMatchingParent, $getNearestNodeOfType } from '@lexical/utils';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $isAtNodeEnd, $patchStyleText } from '@lexical/selection';
import {
  CcfColorPicker,
  CcfTextColorIcon,
  colorData,
  CustomStyleConfig,
  TEXT_COLOR_GROUP,
  HIGHLIGHT_COLOR_GROUP,
  useTranslator,
  CcfBackGroundColorIcon,
  CcfTooltip,
  CcfTextField,
  CcfFileUploadIcon,
} from '@nice-ccf/ui-controls';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { styleMap } from './ccf-rich-text-editor.styles';
import { CUSTOM_STYLE_MAP } from './ccf-editor-utils';
import { BaseEmoji, EmojiData } from 'emoji-mart';
import CcfEmojiPicker from '../ccf-emoji-picker/ccf-emoji-picker';
import { CcfInsertInlineImageButton } from './ccf-inline-image-plugin';
import { CcfVoiceRecorder } from '../ccf-voice-recorder/ccf-voice-recorder';

/**
 * enum for rich text buttons
 */
export enum RICH_TOOLBAR_BUTTONS {
  BOLD = 'bold',
  ITALIC = 'italic',
  UNDERLINE = 'underline',
  NUMBER_LIST = 'number',
  BULLET_LIST = 'bullet',
  LEFT_ALIGN = 'left',
  RIGHT_ALIGN = 'right',
  CENTER_ALIGN = 'center',
  RTL = 'rtl',
  LTR = 'ltr',
}

const KEY_NAMES = {
  ENTER: 'Enter',
  NUMPAD_ENTER: 'NumpadEnter',
  RIGHT_SHIFT: 'ShiftRight',
  LEFT_SHIFT: 'ShiftLeft',
  KEY_B: 'KeyB',
  KEY_I: 'KeyI',
  KEY_U: 'KeyU',
};

export interface CcfEditorToolbarProps {
  editor?: LexicalEditor | any;
  fileUpload ?: React.MutableRefObject<any>;
  getFileInfo ?: (file: any) => void;
}
/**
 * Component displays Rich text Editor controls
 * @returns Rich text Editor controls
 * ```
 * @example
 * <CcfEditorToolbar/>
 * ```
 */
export function CcfEditorToolbar(props: CcfEditorToolbarProps) {
  const theme: Theme = useTheme();
  const editorToolbarStyles = ccfEditorToolbarStyles(theme);
  const { editor, fileUpload,getFileInfo } = props;

  const textColorButtonRef = useRef<HTMLButtonElement>(null);
  const highlightColorButtonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElFS, setAnchorElFS] = useState(null);
  const [selectedFontIndex, setSelectedFontIndex] = useState(1);
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(1);
  const open = Boolean(anchorEl);
  const openFontSize = Boolean(anchorElFS);
  const options = [
    { value: 'FontAndaleMono', label: 'Andale Mono' },
    { value: 'FontArial', label: 'Arial' },
    { value: 'FontArialBlack', label: 'Arial Black' },
    { value: 'FontBookAntiqua', label: 'Book Antiqua' },
    { value: 'FontComicSansMS', label: 'Comic Sans MS' },
    { value: 'FontCourierNew', label: 'Courier New' },
    { value: 'FontGeorgia', label: 'Georgia' },
    { value: 'FontHelvetica', label: 'Helvetica' },
    { value: 'FontImpact', label: 'Impact' },
    { value: 'FontMalgunGothic', label: 'Malgun Gothic' },
    { value: 'FontTahoma', label: 'Tahoma' },
    { value: 'FontTerminal', label: 'Terminal' },
    { value: 'FontTimesNewRoman', label: 'Times New Roman' },
    { value: 'FontTrebuchetMs', label: 'Trebuchet MS' },
    { value: 'FontVerdana', label: 'Verdana' },
  ];
  const optionsFontSize = [
    { value: 'txtExsmall' },
    { value: 'txtSmall' },
    { value: 'txtRegular' },
    { value: 'txtLarge' },
    { value: 'txtExlarge' },
  ];
  const fontStyleMap: { [key: string]: React.CSSProperties } = styleMap;

  const [translate] = useTranslator();

  const [highlightBoldBtn, setHighlightBoldBtn] = useState(false);
  const [highlightUnderlineBtn, setHighlightUnderLineBtn] = useState(false);
  const [highlightItalicBtn, setHighlightItalicBtn] = useState(false);
  const [highlightBulletedListBtn, setHighlightBulletListBtn] = useState(false);
  const [highlightNumberedListBtn, setHighlightNumberedListBtn] =
    useState(false);
  const [highlightAlignBtn, setHighlightAlignBtn] = useState(
    RICH_TOOLBAR_BUTTONS.LEFT_ALIGN
  );
  const selectedAlignment = useRef<
    | RICH_TOOLBAR_BUTTONS.LEFT_ALIGN
    | RICH_TOOLBAR_BUTTONS.RIGHT_ALIGN
    | RICH_TOOLBAR_BUTTONS.CENTER_ALIGN
  >(RICH_TOOLBAR_BUTTONS.LEFT_ALIGN);
  const [highlightDirectionBtn, setHighlightDirectionBtn] = useState(
    RICH_TOOLBAR_BUTTONS.LTR
  );
  const selectedDirection = useRef<
    RICH_TOOLBAR_BUTTONS.LTR | RICH_TOOLBAR_BUTTONS.RTL
  >(RICH_TOOLBAR_BUTTONS.LTR);
  const EmojiButtonRef = useRef<HTMLButtonElement>(null);
  const [isInsertTableClicked, setInsertTableClicked] = useState(false);
  const [isInsertTableBtnDisabled, setInsertTableBtnDisabled] = useState(true);
  const [tableInfo, updateTableInfo] = useState({
    columns: '',
    rows: '',
  });
  const [showURLInput, setURLInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const inlineImageButtonRef = useRef<HTMLLabelElement>(null);

  /**
   * Method to make Text Input bold
   * @param e - SyntheticEvent<HTMLButtonElement>
   * @example onBoldClick(e);
   */
  const onBoldClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, RICH_TOOLBAR_BUTTONS.BOLD);
    setHighlightBoldBtn(!highlightBoldBtn);
  };
  /**
   * Method to make Text Input Italic
   * @param e - SyntheticEvent<HTMLButtonElement>
   * @example onItalicClick(e);
   */
  const onItalicClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, RICH_TOOLBAR_BUTTONS.ITALIC);
    setHighlightItalicBtn(!highlightItalicBtn);
  };
  /**
   * Method to make Text Input Underline
   * @param e - SyntheticEvent<HTMLButtonElement>
   * @example onUnderLineClick(e);
   */
  const onUnderLineClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, RICH_TOOLBAR_BUTTONS.UNDERLINE);
    setHighlightUnderLineBtn(!highlightUnderlineBtn);
  };

  /**
   * Method to make Text Input Ordered List
   * @example -
   * ```
   * onOrderedListClick();
   * ```
   */
  const onUnOrderedListClick = () => {
    if (!highlightBulletedListBtn) {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      setHighlightBulletListBtn(!highlightBulletedListBtn);
      setHighlightNumberedListBtn(false);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      setHighlightBulletListBtn(false);
    }
  };
  /**
   * Method to make Text Input Unordered List
   * @example onUnOrderedListClick();
   */
  const onOrderedListClick = () => {
    console.log('Ordered List Button Clicked');
    if (!highlightNumberedListBtn) {
      console.log('Inserting Ordered List');
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      setHighlightNumberedListBtn(true);
      setHighlightBulletListBtn(false);
    } else {
      console.log('Removing List');
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      setHighlightBulletListBtn(false);
      setHighlightNumberedListBtn(false);
    }
  };

  /**
   * Method to make Text Input aligned
   *  @param direction - direction
   * @example applyAlignment(left);
   */
  const applyAlignment = (direction: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, direction);
    setHighlightAlignBtn(direction as RICH_TOOLBAR_BUTTONS);
    selectedAlignment.current = direction as
      | RICH_TOOLBAR_BUTTONS.LEFT_ALIGN
      | RICH_TOOLBAR_BUTTONS.RIGHT_ALIGN
      | RICH_TOOLBAR_BUTTONS.CENTER_ALIGN;
    editor.focus();
  };

  /**
   * Method to highlight text or change text color
   * @param id - Id
   * @param group - Color Group
   * @example onColorSelect(id, group);
   */
  const onColorSelect = (id: string, group: string) => {
    const colorDetails: CustomStyleConfig = colorData.filter(
      (color: { id: string | null }) => color.id === id
    );
    const selectedColor = colorDetails ? colorDetails[0]?.value : '';
    let style = {};
    //If id is not available/ colorDetails are not available i.e. agent has been selected rest option
    if (group === HIGHLIGHT_COLOR_GROUP) {
      style = {
        'background-color': selectedColor ?? theme?.palette?.text?.white,
      };
    } else {
      style = { color: selectedColor ?? theme?.palette?.text?.black };
    }
    editor.update(() => {
      const selection = $getSelection() as RangeSelection;
      if (selection) {
        $patchStyleText(selection, style);
      }
    });
  };

  /**
   * Method to change the font style/family change
   * @param selectedFont - selected font
   * @example changeFontStyle('FontArial');
   */
  const changeFontFamily = (selectedFont: string) => {
    const fontStyle =
      (Object.values(CUSTOM_STYLE_MAP[selectedFont])[0] as string) ?? '';
    editor.update(() => {
      const selection = $getSelection() as RangeSelection;
      if (selection && fontStyle) {
        $patchStyleText(selection, { 'font-family': fontStyle });
      }
    });
  };
  /**
   * Method to change the font size
   * @param selectedSize - SyntheticEvent<HTMLButtonElement>
   * @example changeFontSize('12');
   */
  const changeFontSize = (selectedSize: string) => {
    const fontSize =
      (Object.values(CUSTOM_STYLE_MAP[selectedSize])[0] as string) ?? '';
    editor.update(() => {
      const selection = $getSelection() as RangeSelection;
      if (selection && fontSize)
        $patchStyleText(selection, { 'font-size': fontSize });
    });
  };

  /**
   * method to handle the font family change
   * @example handleMenuItemClick();
   */
  const handleMenuItemClick = (index: number) => {
    changeFontFamily(options[index].value);
    setSelectedFontIndex(index);
    setAnchorEl(null);
  };

  /**
   * method to handle the font family size
   * @example handleMenuItemClickFontSize();
   */
  const handleMenuItemClickFontSize = (index: number) => {
    changeFontSize(optionsFontSize[index].value);
    setSelectedSizeIndex(index);
    setAnchorElFS(null);
  };

  /**
   * Method to get selected node
   *  @param selection - selection
   *  @returns -  TextNode | Element node
   * @example getSelectedNode();
   */
  const getSelectedNode = (
    selection: RangeSelection
  ): TextNode | ElementNode => {
    const anchor = selection.anchor;
    const focus = selection.focus;
    const anchorNode = anchor.getNode();
    const focusNode = focus.getNode();
    if (anchorNode === focusNode) {
      return anchorNode;
    }
    const isBackward = selection.isBackward();
    if (isBackward) {
      return $isAtNodeEnd(focus) ? anchorNode : focusNode;
    } else {
      return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
    }
  };

  /**
   * Method to handle text direction change
   * @param directionStyle - direction style
   * @param alignmentStyle - alignment style
   * @example applyDirection('RTL', 'right');
   */
  const applyDirection = (
    directionStyle: RICH_TOOLBAR_BUTTONS.LTR | RICH_TOOLBAR_BUTTONS.RTL,
    alignmentStyle: ElementFormatType
  ) => {
    editor.update(() => {
      const selection = $getSelection() as RangeSelection;
      const currentNode = getSelectedNode(selection);
      const parentNode = currentNode.getParent();
      if ($isParagraphNode(currentNode)) {
        currentNode.setDirection(directionStyle);
        currentNode.setFormat(alignmentStyle);
      } else if ($isParagraphNode(parentNode)) {
        parentNode.setDirection(directionStyle);
        parentNode.setFormat(alignmentStyle);
      }
    });
    setHighlightDirectionBtn(directionStyle);
    applyAlignment(alignmentStyle);
    selectedDirection.current = directionStyle;
  };

  /**
   * Method to handle emoji selection and insert into the editor
   * @param emojiData - Selected emoji object
   * @example handleEmojiSelect(emojiData);
   */
  const onEmojiSelect = (emojiData: EmojiData) => {
    const { native } = emojiData as BaseEmoji;
    editor.update(() => {
      const selection = $getSelection() as RangeSelection;
      if (selection) {
        const emojiNode = $createTextNode(native); // to create text node with selected emoji data
        $insertNodes([emojiNode]); // to insert node at the cursor position in editor
      }
    });
  };

  /**
   * Function to reset the form state
   * @example resetFormData()
   */
  const resetFormData = () => {
    setInsertTableBtnDisabled(true);
    updateTableInfo({
      rows: '',
      columns: '',
    });
  };

  /**
   *
   * @param row - Number of rows entered
   * @param col - Number of columns entered
   * @returns boolean value if the entered row/col are valid
   * @example checkForRowColumnValidation(3,2)
   */
  const checkForRowColumnValidation = (row: number, col: number) => {
    const MAX_ROWS = 50;
    const MAX_COLUMNS = 50;
    if (row <= 0 || row > MAX_ROWS) return true;
    if (col <= 0 || col > MAX_COLUMNS) return true;
    return false;
  };

  /**
   * Function to handle the input field characters validation
   * @param event - input field change event
   * @example handleInputChange
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue: string = event.target.value;
    inputValue = inputValue.replace(/[^0-9]/g, ''); // Remove all non-numeric characters
    event.target.value = inputValue; // Assign the updated input value back to the input
  };

  /**
   * Method to handle link selection
   * @param e - SyntheticEvent<HTMLButtonElement>
   * @example onLinkSelect(e);
   */
  const onLinkSelect = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    editor.update(() => {
      const selection = $getSelection() as RangeSelection;
      if (selection && !selection.isCollapsed()) {
        let url = '';
        const node = getSelectedNode(selection);
        const parent = node.getParent();
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          const linkParent = $findMatchingParent(node, $isLinkNode);
          if (linkParent) {
            url = linkParent.getURL();
          } else if ($isLinkNode(node)) {
            url = node.getURL();
          }
        }
        setUrlValue(url);
        setURLInput(true);
      }
    });
  };

  /**
   * Method to handle URL change
   * @param e - SyntheticEvent<HTMLButtonElement>
   * @example onURLChange(e);
   */
  const onURLChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setUrlValue(e.target.value);

  /**
   * method to handle link selection
   * @example onLinkInputKeyDown();
   */
  const onLinkInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEY_NAMES.ENTER) {
      confirmLink();
    }
  };

  /**
   * Handles the change in the input field when a URL is pasted.
   * @param event - The clipboard event triggered by pasting.
   * @example onPasteChange(e).
   */
  const onPasteChange = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const url = event.clipboardData.getData('text');
    setUrlValue(url);
  };

  /**
   * Method to handle link selection
   * @example confirmLink();
   */
  const confirmLink = () => {
    if(urlValue) {
      const targetUrl = {
        url: urlValue,
        target: '_blank',
      };
      if(!targetUrl.url.includes('http://') && !targetUrl.url.includes('https://')){
        targetUrl.url = 'https://' + targetUrl.url;
      }
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, targetUrl);
      setURLInput(false);
      setUrlValue('');
    }
  };

  /**
   * Method to remove link
   * @param e - SyntheticEvent<HTMLButtonElement>
   * @example onLinkRemove(e);
   */
  const onLinkRemove = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    editor.update(() => {
      const selection = $getSelection() as RangeSelection;
      if (selection && !selection.isCollapsed()) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    });
  };

  return (
    <div style={editorToolbarStyles.toolbarContainer as React.CSSProperties}>
      <Button sx={ editorToolbarStyles.btnContainer } size='small' onKeyDown={(e) => {e.key === 'Enter' && EmojiButtonRef?.current?.click()}} name={translate('addEmoji')} data-testid = 'emoji'><CcfEmojiPicker onSelect={(emoji) => onEmojiSelect(emoji)} ref={EmojiButtonRef} data-testid='emoji-select'/></Button>
       <div className={styles.renderFileiconCls}>
       <input
         type="file"
         hidden
         ref={fileUpload}
         multiple
         onChange={getFileInfo}
       />
       <CcfFileUploadIcon
         className={styles.uploadIcon}
         onClick={() => {
           fileUpload?.current?.click();
         }}
       ></CcfFileUploadIcon>
     </div>
      <CcfTooltip title={translate('addInlineImage')}>
        <Button sx={editorToolbarStyles.btnContainer} size='small'  data-testid='inline-image' onKeyDown={(e) => { e.key === KEY_NAMES.ENTER && inlineImageButtonRef?.current?.click() }}>
          <CcfInsertInlineImageButton {...{ activeEditor: editor }} ref={inlineImageButtonRef} />
        </Button>
      </CcfTooltip>
      <CcfTooltip title={translate('removeLink')}>
        <Button
          sx={editorToolbarStyles.button}
          size="small"
          onMouseDown={(e) => onLinkRemove(e)}
          onClick={(e) => onLinkRemove(e)}
        >
          <LinkOffIcon />
        </Button>
      </CcfTooltip>
      <CcfTooltip title={translate('addLink')}>
        <Button
          sx={editorToolbarStyles.button}
          size="small"
          onMouseDown={(e) => onLinkSelect(e)}
          onClick={(e) => onLinkSelect(e)}
        >
          <LinkIcon />
        </Button>
      </CcfTooltip>
      {showURLInput && (
        <>
          <CcfTextField
            id="linkTextInput"
            size="small"
            fullWidth
            style={editorToolbarStyles.linkBox}
            placeholder={translate('exampleEmail')}
            variant="outlined"
            value={urlValue}
            onChange={onURLChange}
            onKeyDown={onLinkInputKeyDown}
            onPaste={onPasteChange}
          />
          <Button
            sx={editorToolbarStyles.button}
            size="small"
            onMouseDown={confirmLink}
            onClick={confirmLink}
          >
            <CheckIcon
              style={editorToolbarStyles.btnCheck}
              color={urlValue ? 'inherit' : 'disabled'}
            />
          </Button>
        </>
      )}
      <Tooltip title={translate('bold')}>
      <Button
        sx={{
        ...(highlightBoldBtn
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('bold')}
        onMouseDown={onBoldClick}
        data-testid="bold"
      >
        <FormatBold />
      </Button>
      </Tooltip>
      <Tooltip title={translate('underline')}>
      <Button
        sx={{
        ...(highlightUnderlineBtn === true
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('underline')}
        onMouseDown={onUnderLineClick}
        data-testid="underline"
      >
        <FormatUnderlined />
      </Button>
      </Tooltip>
      <Tooltip title={translate('italic')}>
      <Button
        sx={{
        ...(highlightItalicBtn === true
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('italic')}
        onMouseDown={onItalicClick}
        data-testid="italic"
      >
        <FormatItalic />
      </Button>
      </Tooltip>
      <Tooltip title={translate('bulletedList')}>
      <Button
        sx={{
        ...(highlightBulletedListBtn === true
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('bulletedList')}
        onClick={onUnOrderedListClick}
        data-testid="bulleted-list"
      >
        <FormatListBulleted />
      </Button>
      </Tooltip>
      <Tooltip title={translate('numberedList')}>
      <Button
        sx={{
        ...(highlightNumberedListBtn === true
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('numberedList')}
        onClick={onOrderedListClick}
        data-testid="numberedList"
      >
        <FormatListNumbered />
      </Button>
      </Tooltip>
      <Tooltip title={translate('leftAlign')}>
      <Button
        sx={{
        ...(highlightAlignBtn === 'left'
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('leftAlign')}
        onClick={() => applyAlignment(RICH_TOOLBAR_BUTTONS.LEFT_ALIGN)}
        data-testid="left-align"
      >
        <FormatAlignLeft />
      </Button>
      </Tooltip>
      <Tooltip title={translate('centerAlign')}>
      <Button
        sx={{
        ...(highlightAlignBtn === 'center'
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('centerAlign')}
        onClick={() => applyAlignment(RICH_TOOLBAR_BUTTONS.CENTER_ALIGN)}
        data-testid="center-align"
      >
        <FormatAlignCenter />
      </Button>
      </Tooltip>
      <Tooltip title={translate('rightAlign')}>
      <Button
        sx={{
        ...(highlightAlignBtn === 'right'
          ? editorToolbarStyles.buttonActive
          : undefined),
        ...editorToolbarStyles.button,
        }}
        name={translate('rightAlign')}
        onClick={() => applyAlignment(RICH_TOOLBAR_BUTTONS.RIGHT_ALIGN)}
        data-testid="right-align"
      >
        <FormatAlignRight />
      </Button>
      </Tooltip>
      <Tooltip title={translate('textColor')}>
        <Button
          sx={editorToolbarStyles.button}
          name={translate('textColor')}
          onKeyDown={(e) => {
            e.key === 'Enter' && textColorButtonRef?.current?.click();
          }}
          data-testid="text-color"
        >
          <CcfColorPicker
            onSelect={(id, group) => id && onColorSelect(id, group)}
            group={TEXT_COLOR_GROUP}
            resetLabel={translate('resetColor')}
            ref={textColorButtonRef}
          >
            <CcfTextColorIcon />
          </CcfColorPicker>
        </Button>
      </Tooltip>
      <Tooltip title={translate('backgroundColor')}>
        <Button
          sx={editorToolbarStyles.button}
          name={translate('backgroundColor')}
          onKeyDown={(e) => {
            e.key === 'Enter' && highlightColorButtonRef?.current?.click();
          }}
          data-testid="bg-color"
        >
          <CcfColorPicker
            onSelect={(id, group) => id && onColorSelect(id, group)}
            group={HIGHLIGHT_COLOR_GROUP}
            resetLabel={translate('resetHighlight')}
            ref={highlightColorButtonRef}
          >
            <CcfBackGroundColorIcon />
          </CcfColorPicker>
        </Button>
      </Tooltip>
      <Tooltip title={translate('fontFamily')}>
        <Button
          sx={editorToolbarStyles.button}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          disableElevation
          onClick={(e: any) => {
            setAnchorEl(e.currentTarget);
          }}
          endIcon={<KeyboardArrowDownIcon />}
          data-testid="font-style"
        >
          <FontDownloadIcon sx={editorToolbarStyles.icon} />
        </Button>
      </Tooltip>
      <Menu
        id="lock-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
        }}
        MenuListProps={{ 'aria-labelledby': 'lock-button', role: 'listbox' }}
        data-testid="lock-menu"
      >
        {options.map((option, index) => (
          <MenuItem
            key={option.label}
            style={fontStyleMap[option.value]}
            selected={index === selectedFontIndex}
            onClick={() => handleMenuItemClick(index)}
            data-testid={`font-style-${index}`}
          >
            {option?.label}
          </MenuItem>
        ))}
      </Menu>
      <Tooltip title={translate('fontSize')}>
        <Button
          sx={editorToolbarStyles.button}
          aria-haspopup="true"
          aria-expanded={openFontSize ? 'true' : undefined}
          disableElevation
          onClick={(e: any) => {
            setAnchorElFS(e.currentTarget);
          }}
          endIcon={<KeyboardArrowDownIcon />}
          data-testid="font-size"
        >
          <TextFieldsIcon sx={editorToolbarStyles.icon} />
        </Button>
      </Tooltip>
      <Menu
        id="lock-menu"
        anchorEl={anchorElFS}
        open={openFontSize}
        onClose={() => {
          setAnchorElFS(null);
        }}
        MenuListProps={{ 'aria-labelledby': 'lock-button', role: 'listbox' }}
      >
        {optionsFontSize.map((option: any, index) => (
          <MenuItem
            key={option.value}
            style={fontStyleMap[option.value]}
            selected={index === selectedSizeIndex}
            onClick={() => handleMenuItemClickFontSize(index)}
            data-testid={`font-size-${index}`}
          >
            {translate(option?.value)}
          </MenuItem>
        ))}
      </Menu>
      <CcfTooltip title={translate('textDirectionltr')}>
        <Button
          size="small"
          sx={{
            ...editorToolbarStyles.button,
            ...(highlightDirectionBtn === RICH_TOOLBAR_BUTTONS.LTR &&
              editorToolbarStyles.buttonActive),
          }}
          name={translate('textDirectionrtl')}
          onClick={() =>
            applyDirection(
              RICH_TOOLBAR_BUTTONS.LTR,
              RICH_TOOLBAR_BUTTONS.LEFT_ALIGN
            )
          }
          data-testid="ltr"
        >
          <FormatTextdirectionLToR />
        </Button>
      </CcfTooltip>
      <CcfTooltip title={translate('textDirectionrtl')}>
        <Button
          size="small"
          sx={{
            ...editorToolbarStyles.button,
            ...(highlightDirectionBtn === RICH_TOOLBAR_BUTTONS.RTL &&
              editorToolbarStyles.buttonActive),
          }}
          name={translate('textDirectionrtl')}
          onClick={() =>
            applyDirection(
              RICH_TOOLBAR_BUTTONS.RTL,
              RICH_TOOLBAR_BUTTONS.RIGHT_ALIGN
            )
          }
          data-testid="rtl"
        >
          <FormatTextdirectionRToL />
        </Button>
      </CcfTooltip>
      <CcfTooltip title={translate('record')}>
          <CcfVoiceRecorder enableRecording={true} />
        </CcfTooltip>
      <CcfTooltip title={translate('insertTable')}>
        <Button
          size="small"
          sx={{ ...editorToolbarStyles.button }}
          name={translate('insertTable')}
          onClick={() => setInsertTableClicked(true)}
        >
          <TableChartOutlined sx={editorToolbarStyles.table} />
        </Button>
      </CcfTooltip>
      <Modal
        open={isInsertTableClicked}
        onClose={() => {
          resetFormData();
          setInsertTableClicked(false);
        }}
        aria-labelledby="modal-insert-table"
        aria-describedby="modal-table-data"
      >
        <Box sx={editorToolbarStyles.modalContainer}>
          <Box sx={editorToolbarStyles.modalContentContainer}>
            <Box sx={editorToolbarStyles.modalContent}>
              <Box>
                <Typography sx={editorToolbarStyles.title}>
                  {translate('insertTable')}
                </Typography>
              </Box>
              <Box sx={editorToolbarStyles.inputdiv}>
                <TextField
                  sx={editorToolbarStyles.inputBtn}
                  id="outlined-basic"
                  label={translate('rows')}
                  type="text"
                  onChange={(e) => {
                    setInsertTableBtnDisabled(
                      checkForRowColumnValidation(
                        Number(e.target.value),
                        Number(tableInfo.columns)
                      )
                    );
                    updateTableInfo({
                      ...tableInfo,
                      rows: e.target.value,
                    });
                  }}
                  placeholder={translate('noOfRowsColumns')}
                  size="small"
                  variant="outlined"
                  onInput={handleInputChange}
                />
              </Box>
              <Box sx={editorToolbarStyles.inputdiv}>
                <TextField
                  sx={editorToolbarStyles.inputBtn}
                  id="outlined-basic"
                  label={translate('columns')}
                  type="text"
                  onChange={(e) => {
                    setInsertTableBtnDisabled(
                      checkForRowColumnValidation(
                        Number(tableInfo.rows),
                        Number(e.target.value)
                      )
                    );
                    updateTableInfo({
                      ...tableInfo,
                      columns: e.target.value,
                    });
                  }}
                  placeholder={translate('noOfRowsColumns')}
                  size="small"
                  variant="outlined"
                  onInput={handleInputChange}
                />
              </Box>
            </Box>
            <Box sx={editorToolbarStyles.closeButtonColumn}>
              <IconButton
                aria-label="close"
                onClick={() => {
                  resetFormData();
                  setInsertTableClicked(false);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={editorToolbarStyles.buttonsContainer}>
            <Button
              variant="contained"
              disabled={isInsertTableBtnDisabled}
              onClick={() => {
                //reset to initial state
                resetFormData();
                setInsertTableClicked(false);
                editor.dispatchCommand(INSERT_TABLE_COMMAND, tableInfo);
              }}
              aria-label="confirm"
            >
              <Typography fontSize={'inherit'}>
                {translate('confirm')}
              </Typography>
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
export default memo(CcfEditorToolbar);
